import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import TextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import IconsError from '@mui/icons-material/Error';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import { Theme } from '@mui/material/styles';
import useRoutes from '../../../../../components/_hook/useRoutes';
import { IValue } from '../../../../../components/_select/_module/Select';
import BaseIconButton from '../../../../../components/_iconButton/_module/BaseIconButton';
import SelectCurrency from '../../../../../components/_select/SelectCurrency';
import CopyIconButton from '../../../../../components/_iconButton/CopyIconButton';
import { IProductSchema } from '../../../../../../graphql/type/product';
import useLanguage from '../../../../../components/_hook/useLanguage';
import SchemaCompanyName from '../../../../../components/_schema/SchemaCompanyName';
import useKeepitalRoutes from '../../../../../components/_hook/useKeepitalRoutes';

interface IItem extends IProductSchema {
  key: string;
  variationId?: string;
  priceCurrency?: string;
  priceFinal?: number;
  variation?: IProductSchema['variations'][0];
}

interface IProps {
  item: IItem;
  setTableRefreshKey?: (tableRefreshKey: number) => void;
  tableRefreshKey?: number;
  isEditable?: boolean;
  onChange?: (item: IItem) => void;
}

const useStyles = makeStyles<Theme>((theme) => ({
  containerSelectCurrency: {
    width: '120px',
    marginRight: theme.spacing(0.5),
  },
  typographyCurrentDisplayedPrice: { color: 'red' },
}));

export default function DropshippedProductPriceManagementTableItem(
  props: IProps
) {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    item,
    setTableRefreshKey,
    isEditable,
    onChange,
    tableRefreshKey,
  } = props;
  const { path } = useRoutes();
  const { getText } = useLanguage();
  const { path: keepitalPath } = useKeepitalRoutes();
  const [priceCurrency, setPriceCurrency] = useState<string>(
    item.priceCurrency || ''
  );
  const [priceFinal, setPriceFinal] = useState<number>(item.priceFinal || 0);

  useEffect(() => {
    setPriceCurrency(item.priceCurrency || '');
    setPriceFinal(item.priceFinal || 0);
  }, [JSON.stringify(item), tableRefreshKey]);

  let salesPriceForDropshippingCurrency;
  let salesPriceForDropshippingFinal = 0;
  const dropshippingRequestProduct = item.dropshippingProductRequestData;
  const sourceProduct = item.sourceDropshippedProductData;
  const sourceProductVariations =
    sourceProduct && sourceProduct.variations ? sourceProduct.variations : [];
  const sourceProductVariation = sourceProductVariations.find(
    (variation) => variation.id === item.variationId
  );
  if (!item.variation) {
    if (
      dropshippingRequestProduct &&
      dropshippingRequestProduct.customPrices &&
      dropshippingRequestProduct.customPrices.currency &&
      dropshippingRequestProduct.customPrices.final
    ) {
      salesPriceForDropshippingCurrency =
        dropshippingRequestProduct.customPrices.currency;
      salesPriceForDropshippingFinal =
        dropshippingRequestProduct.customPrices.final;
    } else {
      if (
        sourceProduct &&
        sourceProduct.dropshipping &&
        sourceProduct.dropshipping.prices &&
        sourceProduct.dropshipping.prices.final
      ) {
        salesPriceForDropshippingCurrency =
          sourceProduct.dropshipping.prices.currency;
        salesPriceForDropshippingFinal =
          sourceProduct.dropshipping.prices.final;
      }
    }
  } else {
    let variationCustomPrices;

    if (sourceProduct && sourceProduct.prices) {
      salesPriceForDropshippingCurrency = sourceProduct.prices.currency;
    }

    if (
      dropshippingRequestProduct &&
      dropshippingRequestProduct.customPrices &&
      dropshippingRequestProduct.customPrices.variations &&
      dropshippingRequestProduct.customPrices.variations.length
    ) {
      variationCustomPrices = dropshippingRequestProduct.customPrices.variations.find(
        (variation) => variation.variationID === item.variationId
      );
    }

    if (
      variationCustomPrices &&
      variationCustomPrices.prices &&
      variationCustomPrices.prices.final
    ) {
      salesPriceForDropshippingFinal = variationCustomPrices.prices.final;
    } else {
      if (
        sourceProductVariation &&
        sourceProductVariation.dropshipping &&
        sourceProductVariation.dropshipping.prices &&
        sourceProductVariation.dropshipping.prices.final
      ) {
        salesPriceForDropshippingFinal =
          sourceProductVariation.dropshipping.prices.final;
      }
    }
  }

  return (
    <>
      <TableCell align="left">
        {Boolean(item.variation) && (
          <>
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <BaseIconButton size="small">
              <SubdirectoryArrowRightIcon fontSize="small" />
            </BaseIconButton>
          </>
        )}
        <Tooltip title={item._id}>
          <Button
            color="primary"
            component="a"
            href={keepitalPath('company.showcase.product.index', {
              origin:
                item.companyData && item.companyData.origin
                  ? item.companyData.origin.toLowerCase()
                  : '',
              companySlug:
                item.companyData && item.companyData.slug
                  ? item.companyData.slug
                  : '',
              productSlug: item.slug,
            })}
            target="_blank"
          >
            {item._id.substr(0, 6)}
          </Button>
        </Tooltip>
        <CopyIconButton iconHtmlFontSize="0.8rem" copyText={item._id} />
      </TableCell>
      <TableCell align="left">
        <Grid container alignItems="center">
          {getText(item.name)}
          {item.variation && item.variation.attributes && (
            <>&nbsp;-&gt;&nbsp;{item.variation.attributes.sku}</>
          )}
        </Grid>
      </TableCell>
      <TableCell align="center">
        {item.sourceDropshippedProductCompanyData && (
          <Button
            color="primary"
            component={Link}
            to={path('product.dropshipped.vendor.detail', {
              companyID: item.company,
              toCompanyID: item.sourceDropshippedProductCompanyID,
            })}
          >
            <SchemaCompanyName
              company={item.sourceDropshippedProductCompanyData}
              showVerification
              showExternalLink
            />
          </Button>
        )}
      </TableCell>
      <TableCell align="center">
        <TextField
          disabled
          type="number"
          value={salesPriceForDropshippingFinal}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {salesPriceForDropshippingCurrency}
              </InputAdornment>
            ),
          }}
        />
      </TableCell>
      <TableCell align="center">
        <Grid container alignItems="center" justifyContent="center">
          {((!item.variationId &&
            priceCurrency === salesPriceForDropshippingCurrency) ||
            (item.variationId &&
              salesPriceForDropshippingCurrency === item.prices.currency)) &&
            priceFinal < salesPriceForDropshippingFinal && (
              <>
                <Tooltip
                  title={t(
                    'displayed_sales_price_cannot_be_less_than_sales_price_for_dropshipper'
                  )}
                >
                  <IconsError fontSize="small" color="error" />
                </Tooltip>
                &nbsp;
              </>
            )}
          {isEditable ? (
            <>
              {!item.variationId && (
                <div className={classes.containerSelectCurrency}>
                  <SelectCurrency<IValue>
                    hideLabel
                    disabled={!isEditable}
                    fullWidth
                    useShortLabel
                    menuPosition="fixed"
                    label=""
                    required
                    queryVariables={{
                      filter: {},
                    }}
                    value={priceCurrency}
                    onChange={(option) => {
                      const value = option ? option.value : '';
                      setPriceCurrency(value);
                      if (onChange) {
                        onChange({
                          ...item,
                          ...{
                            priceCurrency: value,
                            priceFinal,
                          },
                        });
                      }
                    }}
                    placeholder={t('currency')}
                  />
                </div>
              )}
              <TextField
                disabled={!isEditable}
                type="number"
                required
                name="priceFinal"
                value={priceFinal}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  setPriceFinal(value);
                  if (onChange) {
                    onChange({
                      ...item,
                      ...{
                        priceCurrency,
                        priceFinal: value,
                      },
                    });
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {item.variationId ? (
                        <>
                          {item.prices && item.prices.currency
                            ? item.prices.currency
                            : ''}
                        </>
                      ) : (
                        priceCurrency
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </>
          ) : (
            <TextField
              disabled
              required
              value={priceFinal}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {item.variationId ? (
                      <>
                        {item.prices && item.prices.currency
                          ? item.prices.currency
                          : ''}
                      </>
                    ) : (
                      priceCurrency
                    )}
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Grid>
      </TableCell>
    </>
  );
}
